import './style.scss'
import { Input, InputNumber, message, Modal, Switch, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import * as uuid from 'uuid'
import * as Api from '@/api/account'
import {
  CheckOutlined,
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FormOutlined,
  SyncOutlined
} from '@ant-design/icons'

interface IProps {
  apiInfo: any
  onSave: () => void
}

const VipCompany: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['api', 'system'])
  const { apiInfo, onSave } = props
  const [creditEdit, setCreditEdit] = useState(false)
  const [credit, setCredit] = useState<any>()
  const [callbackUrlEdit, setCallbackUrlEdit] = useState(false)
  const [callbackUrl, setCallbackUrl] = useState<any>()
  const [viewToken, setViewToken] = useState(false)
  const [supportV2, setSupportV2] = useState<any>()
  const supportV2Ref = useRef<any>()
  const token = useRef<any>()

  const showToken = useMemo(() => {
    const t = apiInfo?.token
    if (viewToken) {
      return t
    }
    return t?.replace(
      /^(...)(.*)(...)$/,
      (_: any, prefix: any, middle: any, suffix: any) => prefix + middle.replace(/./g, '*') + suffix
    )
  }, [apiInfo?.token, viewToken])

  useEffect(() => {
    supportV2Ref.current = supportV2
  }, [supportV2])

  useEffect(() => {
    if (apiInfo?.token) {
      setCredit(apiInfo.warning_credits_watermark)
      setCallbackUrl(apiInfo.callback_url)
      setSupportV2(apiInfo.support_v2 === 1)
      token.current = apiInfo.token
    }
  }, [apiInfo])

  const refreshToken = () => {
    Modal.confirm({
      title: translate('personal.warning'),
      content: translate('personal.reset_token_confirmation'),
      onOk: () => {
        token.current = uuid.v4().replace(/-/g, '').substring(0, 16)
        save()
      }
    })
  }

  const editCredit = () => {
    setCreditEdit(true)
  }

  const saveEditCredit = () => {
    if (credit < 5000) {
      return message.warning(translate('personal.min_credit_warning'))
    }
    setCreditEdit(false)
    save()
  }

  const editCallbackUrl = () => {
    setCallbackUrlEdit(true)
    setCallbackUrl(apiInfo.callback_url)
  }

  const saveEditCallbackUrl = () => {
    setCallbackUrlEdit(false)
    save()
  }

  const onSupportV2Change = (v: boolean) => {
    if (v) {
      Modal.confirm({
        title: translate('personal.warning'),
        content: translate('personal.enable_v2_confirmation'),
        onOk: () => {
          supportV2Ref.current = true
          save()
        }
      })
    } else {
      supportV2Ref.current = false
      save()
    }
  }

  const save = async () => {
    await Api.editApiInfo({
      token: token.current,
      callback_url: callbackUrl,
      support_v2: supportV2Ref.current ? 1 : 0,
      warning_credits_watermark: credit || 5000
    })
    message.success(translate('system.update_success'))
    onSave()
  }

  return (
    <div className="vip-company">
      <div className="title">
        {translate('personal.details')}
        <span>
          （
          <a target="_blank" href="https://api.hifly.cc/hifly.html" rel="noreferrer">
            {translate('personal.view_documentation')}
          </a>
          ）
        </span>
      </div>
      <div className="form-item">
        <label className="left">{translate('personal.token')}：</label>
        <label className="right">{showToken}</label>

        <Tooltip placement="top" title={translate('system.copy')}>
          <CopyToClipboard
            text={apiInfo.token}
            onCopy={() => {
              message.success(translate('system.copied'))
            }}
          >
            <CopyOutlined />
          </CopyToClipboard>
        </Tooltip>

        <div className="icon" onClick={() => setViewToken(!viewToken)}>
          {!viewToken ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </div>

        <Tooltip placement="top" title={translate('personal.reset_token')}>
          <label className="op" onClick={refreshToken}>
            <SyncOutlined />
          </label>
        </Tooltip>
      </div>
      <div className="form-item">
        <label className="left">{translate('personal.expiry_date')}：</label>
        <label className="right">{dayjs(apiInfo.expire_time * 1000).format('YYYY-MM-DD HH:mm')}</label>
      </div>
      <div className="form-item">
        <label className="left">{translate('personal.callback_url')}：</label>
        <label className="right">
          {callbackUrlEdit ? (
            <Input
              className="form-input"
              placeholder={translate('personal.callback_url_placeholder')}
              value={callbackUrl}
              onChange={(e) => setCallbackUrl(e.target.value)}
              maxLength={100}
            />
          ) : (
            apiInfo.callback_url
          )}
        </label>
        <label className="op">
          {callbackUrlEdit ? (
            <CheckOutlined onClick={saveEditCallbackUrl} />
          ) : (
            <Tooltip placement="top" title={translate('system.edit')}>
              <FormOutlined onClick={editCallbackUrl} />
            </Tooltip>
          )}
        </label>
      </div>

      <div className="form-item">
        <label className="left" style={{ minWidth: 160 }}>
          {translate('personal.enable_v2_events')}：
        </label>
        <Switch checked={supportV2} onChange={onSupportV2Change} />
      </div>

      <div className="form-item">
        <label className="left">{translate('personal.credit_alert')}：</label>
        <label className="right">
          {translate('personal.credit_alert_text')}
          {creditEdit ? (
            <InputNumber value={credit} onChange={(e) => setCredit(e)} min={0} max={999999} maxLength={6} />
          ) : (
            apiInfo.warning_credits_watermark
          )}
          {translate('personal.credit_alert_suffix')}
        </label>
        <label className="op">
          {creditEdit ? (
            <CheckOutlined onClick={saveEditCredit} />
          ) : (
            <Tooltip placement="top" title={translate('system.edit')}>
              <FormOutlined onClick={editCredit} />
            </Tooltip>
          )}
        </label>
      </div>
    </div>
  )
}

export default memo(VipCompany)
